<template>
    <b-modal
      :visible="visible"
      title="UTM Parametrelerini Düzenle"
      @hidden="$emit('close')"
      size="md"
      ok-only
      :ok-title="null"
      hide-footer
    >
      <div>
        <b-form-group
            label="Kısa Link"
            label-for="short_url"
          >
            <b-input-group>
                <b-form-input
                id="link"
                v-model="utmData.short_url"
                :disabled="true"
                trim
                />
                <b-input-group-append>
                <b-button
                    variant="outline-success"
                    size="sm"
                    @click="$emit('copy-emit',utmData)"
                >
                    <feather-icon 
                    icon="CopyIcon"
                    title="Panoya Kopyala"
                    class="cursor-pointer" size="22"
                    ></feather-icon>
                </b-button>
                </b-input-group-append>
            </b-input-group>
        </b-form-group>
        <b-form-group label="Link son hali (önizleme)">
          <div class="preview-link-box" v-html="highlightedPreviewUrl" style="user-select: none;"></div>
        </b-form-group>
        <b-row>
          <b-col
            v-for="(param, idx) in visibleUtmParams"
            :key="idx"
            cols="12"
            class="d-flex mb-2 align-items-center"
          >
            <b-form-input
              v-model="param.key"
              placeholder="Anahtar"
              class="mr-1"
              :readonly="isProtectedKey(param.key)"
              :class="{ 'text-info': isProtectedKey(param.key) }"
              tabindex="-1"
              @focus="editingKey = param.key"
              @input="onKeyInput(idx, $event); editingKey = param.key"
              @blur="editingKey = null"
            />
            <b-form-input
              v-model="param.value"
              placeholder="Değer"
              class="mr-1"
              :readonly="isProtectedKey(param.key, true)"
              :class="{ 'bg-light': isProtectedKey(param.key, true) }"
              @focus="editingKey = param.key"
              @input="editingKey = param.key"
              @blur="editingKey = null"
            />
            <b-button
              v-if="!isProtectedKey(param.key)"
              variant="danger"
              @click="removeParam(idx)"
              size="sm"
            >Sil</b-button>
          </b-col>
          <b-col cols="12" class="mb-2">
            <b-button size="sm" variant="success" @click="addParam">
              + Yeni Parametre
            </b-button>
            <b-button size="sm" variant="primary" class="ml-1" @click="saveUtm" :disabled="saveLoading">
              <b-spinner small v-if="saveLoading" class="mr-1"/> Kaydet
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </template>
  
  <script>
  import {
    BModal, BFormInput, BFormGroup, BButton, BRow, BCol, BSpinner,
    BInputGroupAppend,
    BInputGroup
  } from 'bootstrap-vue'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  
  const PROTECTED_KEYS = ['utm_source', 'utm_medium']
  const HIDDEN_KEYS = ['utm_influencer', 'utm_campaign_secret_key']
  
  export default {
    name: "UtmParameterEditor",
    components: { BModal, BFormInput, BFormGroup, BButton, BRow, BCol, BSpinner, BInputGroupAppend, BInputGroup },
    props: {
      visible: { type: Boolean, default: false },
      utmData: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        utmParams: [],
        hiddenUtmParams: [],
        urlInput: "",
        saveLoading: false,
        editingKey: null, // O an düzenlenen parametre key'i
      }
    },
    computed: {
      visibleUtmParams() {
        // Sadece kullanıcıya gösterilecek olanlar
        return Array.isArray(this.utmParams)
          ? this.utmParams.filter(p => p && !HIDDEN_KEYS.includes(p.key))
          : []
      },
      previewUrl() {
        const urlInput = this.urlInput || ''
        const [base, queryString] = urlInput.split('?')

        // Var olan parametreleri parçala (utm ve diğerleri)
        const existingParams = queryString
          ? queryString.split('&').filter(p => p.includes('=')).map(p => {
              const [key, value] = p.split('=')
              return { key, value: decodeURIComponent(value || '') }
            })
          : []

        // Yeni UTM'leri hazırlıyoruz
        const updatedUtmParams = [
          ...this.visibleUtmParams,
          ...this.hiddenUtmParams,
        ].filter(p => p.key && p.value)

        // Diğer parametreleri (utm_ ile başlamayanları) koruyoruz
        const nonUtmParams = existingParams.filter(p => !p.key.startsWith('utm_'))

        // UTM anahtarlarını güncelleyip ekle (varsa eskisini override eder)
        const merged = [...nonUtmParams]

        for (const utm of updatedUtmParams) {
          const existingIndex = merged.findIndex(p => p.key === utm.key)
          if (existingIndex !== -1) {
            merged[existingIndex].value = utm.value
          } else {
            merged.push({ key: utm.key, value: utm.value })
          }
        }

        const query = merged
          .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
          .join('&')

        return query ? `${base}?${query}` : base
      },
      highlightedPreviewUrl() {
        // Sadece editingKey olan parametreyi sarı yap
        return this.highlightEditingParam(this.previewUrl, this.editingKey)
      }
    },
    watch: {
      utmData: {
        immediate: true,
        handler(val) {
          this.urlInput = (val && typeof val.long_url === "string") ? val.long_url : ""
          this.parseUtmParams(this.urlInput)
          this.ensureProtected()
        }
      }
    },
    methods: {
      parseUtmParams(url) {
        this.utmParams = []
        this.hiddenUtmParams = []
        this.otherParams = [] // UTM olmayan diğer parametreler için
        
        if (!url || typeof url !== 'string') return
        
        const qIndex = url.indexOf('?')
        if (qIndex === -1) return
        
        const query = url.slice(qIndex + 1)
        if (!query) return
        
        const pairs = query.split('&').filter(Boolean)
        
        for (const pair of pairs) {
          const [key, value] = pair.split('=')
          if (key) {
            const paramObj = { key, value: decodeURIComponent(value || '') }
            
            if (key.startsWith('utm_')) {
              // UTM parametreleri
              if (HIDDEN_KEYS.includes(key)) {
                this.hiddenUtmParams.push(paramObj)
              } else {
                this.utmParams.push(paramObj)
              }
            } else {
              // UTM olmayan diğer parametreler (q, category, vb.)
              this.otherParams.push(paramObj)
            }
          }
        }
      },

      isProtectedKey(key, valueField = false) {
        // Value alanı için utm_source ve utm_medium düzenlenebilir.
        if (valueField && PROTECTED_KEYS.includes(key)) return false
        // Key alanı için readonly ve silinemez
        return PROTECTED_KEYS.includes(key)
      },
      ensureProtected() {
        // PROTECTED_KEYS başta ve sadece 1 kez olmalı, silinemez
        const keys = this.utmParams.map(p => p.key)
        PROTECTED_KEYS.forEach((protectedKey, i) => {
          if (!keys.includes(protectedKey)) {
            this.utmParams.splice(i, 0, { key: protectedKey, value: '' })
          }
        })
        // Sıralama: utm_source ve utm_medium daima başta olsun
        this.utmParams.sort((a, b) => {
          const order = { utm_source: 0, utm_medium: 1 }
          return (order[a.key] ?? 99) - (order[b.key] ?? 99)
        })
        // Dublikasyon engelle
        this.utmParams = this.utmParams.filter(
          (p, idx, arr) => !PROTECTED_KEYS.includes(p.key) || arr.findIndex(pp => pp.key === p.key) === idx
        )
      },
      addParam() {
        this.utmParams.push({ key: '', value: '' })
      },
      removeParam(idx) {
        const param = this.visibleUtmParams[idx]
        if (!param || PROTECTED_KEYS.includes(param.key)) return
        // Dizideki gerçek index'i bul
        const realIdx = this.utmParams.findIndex(
          p => p && p.key === param.key && p.value === param.value
        )
        if (realIdx !== -1) {
          this.utmParams.splice(realIdx, 1)
          this.ensureProtected()
        }
      },
      onKeyInput(idx, event) {
        const val = event && event.target ? event.target.value : ''
        // Eğer utm_source veya utm_medium key'ine yazılmaya çalışılırsa engelle
        if (PROTECTED_KEYS.includes(val) && !this.isProtectedKey(val)) {
          this.$set(this.visibleUtmParams, idx, {
            ...this.visibleUtmParams[idx],
            key: ''
          })
        }
        // Dublikasyon engelle
        const keyCounts = {}
        this.utmParams.forEach((p, i) => {
          if (p.key) {
            keyCounts[p.key] = (keyCounts[p.key] || 0) + 1
            if (keyCounts[p.key] > 1 && !PROTECTED_KEYS.includes(p.key)) {
              this.utmParams.splice(i, 1)
            }
          }
        })
      },
      saveUtm() {
        this.saveLoading = true

      const urlInput = this.urlInput || ''
      const [base, queryString] = urlInput.split('?')

      // Mevcut parametreleri nesne olarak al
      const existingParams = queryString
        ? queryString.split('&').filter(p => p.includes('=')).map(p => {
            const [key, value] = p.split('=')
            return { key, value: decodeURIComponent(value || '') }
          })
        : []

      // Yeni UTM parametrelerini topla
      const updatedUtmParams = [
        ...this.visibleUtmParams,
        ...this.hiddenUtmParams,
      ].filter(p => p.key && p.value)

      // UTM dışı parametreleri tut
      const nonUtmParams = existingParams.filter(p => !p.key.startsWith('utm_'))

      // UTM’leri override ederek birleştir
      const finalParams = [...nonUtmParams]

      for (const utm of updatedUtmParams) {
        const existingIndex = finalParams.findIndex(p => p.key === utm.key)
        if (existingIndex !== -1) {
          finalParams[existingIndex].value = utm.value
        } else {
          finalParams.push({ key: utm.key, value: utm.value })
        }
      }

      // Yeni URL'yi oluştur
      const query = finalParams
        .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
        .join('&')

      const newUrl = query ? `${base}?${query}` : base

      const data = {
        short_code: this.utmData.short_code,
        long_url: newUrl
      }
        this.$http.put('/link/long-url', data)
          .then(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Başarılı',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'UTM parametreleri güncellendi.',
              },
            })
            this.$emit('save')
            this.$emit('close')
          })
          .finally(() => {
            this.saveLoading = false
          })
      },
      // Sadece editingKey olan parametreyi sarı ile işaretle
      highlightEditingParam(url, editingKey) {
        if (!editingKey) return url
        // RegExp ile sadece ilgili parametreyi işaretle
        const paramRegex = new RegExp(`(${editingKey}=[^&]*)`)
        return url.replace(paramRegex, '<mark class="highlight-utm">$1</mark>')
      }
    }
  }
  </script>
  
  <style>
  .preview-link-box {
    background: #f8f9fa;
    font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    color: #495057;
    border-radius: 6px;
    padding: 10px 14px;
    min-height: 38px;
    word-break: break-all;
    font-size: 0.97rem;
    line-height: 1.7;
    box-shadow: 0 1px 2px rgba(70,80,90,0.03);
    border: 1px solid #e5e7eb;
    margin-bottom: 0.4rem;
    white-space: pre-wrap;
  }
  .text-info {
    font-weight: bold;
    color: #17a2b8;
  }
  .bg-light {
    background: #f8f9fa !important;
  }
  .form-control[readonly] {
    background: #f8f9fa;
    border: 1px solid #ced4da;
  }
  mark.highlight-utm {
    background: yellow;
    color: #212529;
    padding: 0 2px;
    border-radius: 3px;
  }
  </style>